import { Button, useMobileAndTablet } from '@sonnen/web-ui';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useAppContext } from '../../AppContext/AppContext';
import { AppRoute, nextLabelByPath, noBottomCtaRoutes, skippableRoutes } from '../../constants';
import { useCurrentRoute } from '../../hooks/useCurrentRoute';
import { useOrderFlowNavigation } from '../../hooks/useOrderFlowNavigation';
import { useTranslation } from '../../i18n/i18n';
import { useOfferContext } from '../../OfferContext/OfferContext';
import { OrderFlow } from '../../types';
import { DefaultCtaContent } from '../DefaultCtaContent/DefaultCtaContent';
import { BatteryOptimizationCta } from './BatteryOptimizationCta/BatteryOptimizationCta';
import styles from './MobileCta.module.css';
import { TariffsCta } from './TariffsCta/TariffsCta';

const MobileCta = () => {
  const { offerRequiresManualAddress, orderFlow } = useAppContext();
  const { offer, offerLoading, offerError } = useOfferContext();
  const { t } = useTranslation();
  const { navigateNext } = useOrderFlowNavigation();
  const currentRoute = useCurrentRoute();
  const nextLabel = t(nextLabelByPath[currentRoute] ?? 'offer.offerBtn');
  const mobileAndTablet = useMobileAndTablet();
  const routeWithoutValidation = skippableRoutes.has(currentRoute);
  const tariffsComparisonPage = currentRoute === AppRoute.TARIFFS_COMPARISON;

  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  useEffect(() => {
    const checkOverlayState = () => {
      setIsOverlayOpen(document.body.style.overflow === 'hidden');
    };

    checkOverlayState();

    const observer = new MutationObserver(checkOverlayState);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => observer.disconnect();
  }, []);

  if (!mobileAndTablet) return false;

  const showing =
    !tariffsComparisonPage &&
    !isOverlayOpen &&
    !noBottomCtaRoutes.includes(currentRoute) &&
    Boolean(
      offer ||
        offerLoading ||
        offerError ||
        offerRequiresManualAddress ||
        orderFlow === OrderFlow.selfConsumptionPlus ||
        orderFlow === OrderFlow.batteryOptimization,
    ) &&
    routeWithoutValidation;

  const onOfferBtnClick = () => {
    if (offerLoading || !navigateNext) return;
    navigateNext();
  };

  const nextButton = navigateNext && (
    <Button
      label={nextLabel}
      onClick={onOfferBtnClick}
      dataTestId="offer-card-mobile-btn"
      loading={offerLoading}
      expanded
    />
  );

  let content: React.ReactNode;

  switch (orderFlow) {
    case OrderFlow.selfConsumptionPlus:
      content = (
        <>
          <DefaultCtaContent value={0} dataTestId="self-consumption-plus-cta" />
          {nextButton}
        </>
      );
      break;
    case OrderFlow.batteryOptimization:
      content = <BatteryOptimizationCta />;
      break;
    default:
      content = (
        <>
          <TariffsCta />
          {nextButton}
        </>
      );
  }

  return (
    <div className={clsx(styles.mobileCta, { [styles.showing]: showing })} data-testid="mobile-cta">
      {content}
    </div>
  );
};

export default MobileCta;
